import { Fragment, useMemo, useState, type ReactNode } from 'react';
import Alert from '@mui/material/Alert';
import AlertTitle from '@mui/material/AlertTitle';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import Dialog from '@mui/material/Dialog';
import FormControlLabel from '@mui/material/FormControlLabel';
import FormHelperText from '@mui/material/FormHelperText';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import Step from '@mui/material/Step';
import StepLabel from '@mui/material/StepLabel';
import Stepper from '@mui/material/Stepper';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { useTranslation } from 'react-i18next';
import type { z } from 'zod';
import type { ConfigApi } from '@eis/insuranceservice-sdk';
import {
  gateReportOf,
  releaseTypes,
  REASON_REF_CONFLICT,
  refusalReasonOf,
  type ReleaseType,
  type ServiceMutation,
} from '../api/tarifs';
import {
  manifestBlocks,
  toManifestDocument,
  validateManifestForm,
  type ManifestBlock,
  type ManifestFormValues,
} from '../model/manifest-form';
import { GateReportPanel } from './GateReportPanel';
import { PartnerRolesEditor } from './PartnerRolesEditor';

/**
 * Publishing, in two steps: state the manifest, then face the gate.
 *
 * The split is the domain's, not a UX preference. Step 1 is a document the operator authors and
 * the console can check on its own — shape, ERN grammar, required blocks. Step 2 is the gate,
 * which only the service can run: it resolves every ref against the service that owns it and
 * reprices the golden cases (service summary §3.4). Merging them would mean either sending a
 * manifest the console knew was malformed, or pretending the console can predict a premium.
 *
 * `:validate` runs the gate WITHOUT writing (decision S-10). So step 2 shows what a publish would
 * say, and the publish that follows is a confirmation rather than a gamble. Both answers are
 * rendered by the same panel on purpose — a refused publish must look identical to the refused
 * dry run that predicted it, or the dry run stops being believed.
 *
 * # One wizard for both configuration layers
 *
 * A tarif version and a group tarif version differ in exactly one place: `refs`. Everything else —
 * description, release type, baseline, the three asset maps, the gate, the precondition dance — is
 * the same act on the same kind of artifact. So the difference is a prop (`renderRefs` and the
 * schema), not a second wizard: two copies would drift on the part that is identical and would
 * make the part that genuinely differs harder to see.
 */
export interface PublishWizardProps<TVariables> {
  readonly title: string;
  readonly open: boolean;
  readonly initialValues: ManifestFormValues;
  /** ERNs that may be named as `basedOn` — this generation's published versions. */
  readonly baselines: readonly string[];
  /** The schema that decides what a manifest is for THIS layer. */
  readonly schema: { safeParse: (value: unknown) => z.ZodSafeParseResult<unknown> };
  /**
   * The `refs` block's editor. A single tarif takes free-form `key = ern` lines; a group tarif
   * pins an exact child version and a group core, so it offers pickers instead.
   */
  readonly renderRefs: (props: {
    readonly value: string;
    readonly onChange: (next: string) => void;
    readonly error: string;
  }) => ReactNode;
  /**
   * The documents / workflows / settings editors. Optional, and it defaults to the plain
   * `key = ern` textarea — which is still the honest control for a caller that has no candidates
   * to offer.
   */
  readonly renderBlock?: (props: {
    readonly block: ManifestBlock;
    readonly value: string;
    readonly onChange: (next: string) => void;
    readonly error: string;
  }) => ReactNode;
  readonly validate: ServiceMutation<TVariables, { gateReport?: ConfigApi.GateReport }>;
  readonly publish: ServiceMutation<TVariables, { version?: { ern?: string } }>;
  /**
   * Turns a manifest document into this layer's mutation variables.
   *
   * `activate` is passed alongside rather than folded into the manifest, because it is not part of
   * the manifest: the digest is computed over the refs, and whether this publish also moves the
   * generation's pointer is a decision about the release, not a property of the artifact.
   */
  readonly variablesOf: (
    manifest: Record<string, unknown>,
    options: { readonly activate: boolean },
  ) => TVariables;
  /** False while the generation is being re-read; publishing then has no precondition to quote. */
  readonly preconditionReady: boolean;
  readonly onClose: () => void;
  readonly onPublished: (ern: string | undefined) => void;
}

export function PublishWizard<TVariables>({
  title,
  open,
  initialValues,
  baselines,
  schema,
  renderRefs,
  renderBlock,
  validate,
  publish,
  variablesOf,
  preconditionReady,
  onClose,
  onPublished,
}: PublishWizardProps<TVariables>): ReactNode {
  const { t } = useTranslation('insurance');
  const [values, setValues] = useState<ManifestFormValues>(initialValues);
  const [step, setStep] = useState<0 | 1>(0);
  const [submitted, setSubmitted] = useState(false);
  /**
   * Default ON, because that is what `PublishVersionRequest.activate` means when it is unset, and
   * a checkbox that defaulted the other way would quietly change what publishing does for every
   * operator who never noticed it (prompt 14.5).
   *
   * Turning it OFF is the staged release: publish, review, activate later from the version list.
   */
  const [activate, setActivate] = useState(true);

  const issues = useMemo(() => validateManifestForm(values, schema), [values, schema]);

  /**
   * Every complaint about one field, joined. `at` is appended rather than interpolated into the
   * message: the location is a manifest key or a raw line, not prose, and putting it inside a
   * translated sentence would make every translator responsible for keeping it verbatim.
   */
  const issuesFor = (field: keyof ManifestFormValues): string =>
    issues
      .filter((issue) => issue.field === field)
      .map((issue) => {
        const message = t(issue.messageKey as never);
        return issue.at === undefined || issue.at === '' ? message : `${message}: ${issue.at}`;
      })
      .join(' · ');

  // A gate report from either call. `refused` is derived from which call produced it rather than
  // from `passed` alone — a report attached to an error is a refusal either way.
  const refusalReport = gateReportOf(validate.error) ?? gateReportOf(publish.error);
  const report: ConfigApi.GateReport | undefined = refusalReport ?? validate.data?.gateReport;
  const refused = refusalReport !== undefined;
  const refusalReason = refusalReasonOf(validate.error) ?? refusalReasonOf(publish.error);
  const conflict = validate.conflict ?? publish.conflict;

  const set = (field: keyof ManifestFormValues, value: string): void => {
    setValues((current) => ({ ...current, [field]: value }));
  };

  const close = (): void => {
    setValues(initialValues);
    setStep(0);
    setSubmitted(false);
    setActivate(true);
    validate.reset();
    publish.reset();
    onClose();
  };

  return (
    <Dialog open={open} onClose={close} fullWidth maxWidth="md">
      <DialogTitle>{title}</DialogTitle>
      <DialogContent>
        <Stepper activeStep={step} sx={{ my: 2 }}>
          <Step>
            <StepLabel>{t('tarifs.publish.steps.manifest')}</StepLabel>
          </Step>
          <Step>
            <StepLabel>{t('tarifs.publish.steps.gate')}</StepLabel>
          </Step>
        </Stepper>

        {conflict !== null && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            <AlertTitle>{t('tarifs.publish.conflict.title')}</AlertTitle>
            {t('tarifs.publish.conflict.description')}
          </Alert>
        )}

        {step === 0 ? (
          <Stack spacing={2} sx={{ pt: 1 }}>
            <Typography variant="body2" color="text.secondary">
              {t('tarifs.publish.manifestHint')}
            </Typography>

            <TextField
              label={t('tarifs.publish.fields.description')}
              value={values.description}
              onChange={(event) => {
                set('description', event.target.value);
              }}
              required
              fullWidth
              error={submitted && issuesFor('description') !== ''}
              helperText={
                submitted && issuesFor('description') !== ''
                  ? issuesFor('description')
                  : t('tarifs.publish.fields.descriptionHint')
              }
            />

            <TextField
              select
              label={t('tarifs.publish.fields.releaseType')}
              value={values.releaseType}
              onChange={(event) => {
                set('releaseType', event.target.value);
              }}
              fullWidth
              helperText={t('tarifs.publish.fields.releaseTypeHint')}
            >
              {releaseTypes.map((type: ReleaseType) => (
                <MenuItem key={type} value={type}>
                  {t(`tarifs.releaseType.${type}` as never)}
                </MenuItem>
              ))}
            </TextField>

            <TextField
              select
              label={t('tarifs.publish.fields.basedOn')}
              value={values.basedOn}
              onChange={(event) => {
                set('basedOn', event.target.value);
              }}
              fullWidth
              error={submitted && issuesFor('basedOn') !== ''}
              helperText={
                submitted && issuesFor('basedOn') !== ''
                  ? issuesFor('basedOn')
                  : t('tarifs.publish.fields.basedOnHint')
              }
            >
              <MenuItem value="">{t('tarifs.publish.fields.noBaseline')}</MenuItem>
              {baselines.map((ern) => (
                <MenuItem key={ern} value={ern}>
                  {ern}
                </MenuItem>
              ))}
            </TextField>

            {renderRefs({
              value: values.refs,
              onChange: (next) => {
                set('refs', next);
              },
              error: submitted ? issuesFor('refs') : '',
            })}

            {manifestBlocks
              .filter((block) => block !== 'refs')
              .map((block) => {
                const props = {
                  block,
                  value: values[block],
                  onChange: (next: string) => {
                    set(block, next);
                  },
                  error: submitted ? issuesFor(block) : '',
                };
                return (
                  <Fragment key={block}>
                    {renderBlock === undefined ? (
                      <ManifestBlockField {...props} />
                    ) : (
                      renderBlock(props)
                    )}
                  </Fragment>
                );
              })}

            {/* The fifth block (SDD-011): structured, and the policyholder row locked (P-6). */}
            <PartnerRolesEditor
              value={values.partnerRoles}
              onChange={(next) => {
                setValues((current) => ({ ...current, partnerRoles: next }));
              }}
              errors={
                submitted
                  ? Object.fromEntries(
                      issues
                        .filter((issue) => issue.field === 'partnerRoles')
                        .map((issue) => [issue.at ?? '', t(issue.messageKey as never)]),
                    )
                  : {}
              }
            />

            {/*
              Whether this publish also makes the new version the active one. It is on step 1
              rather than beside the publish button, because it changes what the operator is about
              to ask for and they should decide it while they are still authoring — not in the
              second after a green gate report, when the only thing on their mind is "ship it".
            */}
            <Stack>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={activate}
                    onChange={(event) => {
                      setActivate(event.target.checked);
                    }}
                  />
                }
                label={t('tarifs.publish.fields.activate')}
              />
              <FormHelperText>
                {activate
                  ? t('tarifs.publish.fields.activateOnHint')
                  : t('tarifs.publish.fields.activateOffHint')}
              </FormHelperText>
            </Stack>
          </Stack>
        ) : (
          <Stack spacing={2} sx={{ pt: 1 }}>
            {validate.isPending && (
              <Typography variant="body2">{t('tarifs.publish.gate.running')}</Typography>
            )}
            {report !== undefined && <GateReportPanel report={report} refused={refused} />}
            {refusalReason === REASON_REF_CONFLICT && (
              <Alert severity="error">{t('tarifs.publish.gate.refConflict')}</Alert>
            )}
            {report === undefined && !validate.isPending && validate.isError && (
              <Alert severity="error">{validate.error.message}</Alert>
            )}
            {publish.isSuccess && (
              <Alert severity="success">
                {t('tarifs.publish.published', { ern: publish.data.version?.ern ?? '' })}
              </Alert>
            )}
          </Stack>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={close}>{t('tarifs.publish.actions.cancel')}</Button>

        {step === 0 ? (
          <Button
            variant="contained"
            onClick={() => {
              setSubmitted(true);
              // The console refuses to send a manifest it already knows is malformed. Everything
              // it could not know — do the refs resolve, did the premium move — is the gate's.
              if (issues.length > 0) return;
              setStep(1);
              validate.mutate(variablesOf(toManifestDocument(values), { activate }));
            }}
          >
            {t('tarifs.publish.actions.runGate')}
          </Button>
        ) : (
          <>
            <Button
              onClick={() => {
                setStep(0);
                validate.reset();
                publish.reset();
              }}
            >
              {t('tarifs.publish.actions.back')}
            </Button>
            <Button
              variant="contained"
              // Green publishes. Everything else — a refusal, a still-running gate, a
              // precondition the SDK has not re-established yet — leaves this disabled.
              disabled={
                report === undefined ||
                refused ||
                publish.isPending ||
                publish.isSuccess ||
                !preconditionReady
              }
              onClick={() => {
                publish.mutate(variablesOf(toManifestDocument(values), { activate }), {
                  onSuccess: (data) => {
                    onPublished(data.version?.ern);
                  },
                });
              }}
            >
              {t('tarifs.publish.actions.publish')}
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
}

/** One manifest block: `key = ern`, one per line. */
export function ManifestBlockField({
  block,
  value,
  onChange,
  error,
}: {
  readonly block: ManifestBlock;
  readonly value: string;
  readonly onChange: (next: string) => void;
  readonly error: string;
}): ReactNode {
  const { t } = useTranslation('insurance');
  return (
    <TextField
      label={t(`tarifs.manifest.blocks.${block}` as never)}
      value={value}
      onChange={(event) => {
        onChange(event.target.value);
      }}
      multiline
      minRows={3}
      fullWidth
      slotProps={{ htmlInput: { style: { fontFamily: 'monospace' }, spellCheck: false } }}
      error={error !== ''}
      helperText={error === '' ? t('tarifs.publish.fields.blockHint') : error}
    />
  );
}
